// « Photo Vivante » (2026-11-03): the printed photo is a still from the
// client's own video, so on the page the photo seems to start moving.
// The browser picks the sharpest of a dozen frames and cuts it to the album
// page's shape (200 × 297 mm, portrait). Pure helpers + one DOM routine.
// Guard: livingStill.test.ts

/** Album page shape (width / height), the shape the still is printed in. */
export const PAGE_ASPECT = 200 / 297;
export const STILL_MAX_H = 2400;
export const SAMPLE_FRAMES = 12;

/** Sharpness of a grey image: variance of a 4-neighbour Laplacian. Pure. */
export function sharpness(gray: Uint8ClampedArray | number[], w: number, h: number): number {
  let sum = 0, sumSq = 0, n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lap = 4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - w] - gray[i + w];
      sum += lap; sumSq += lap * lap; n++;
    }
  }
  if (!n) return 0;
  const mean = sum / n;
  return sumSq / n - mean * mean;
}

/** The biggest centred rectangle of `aspect` (w/h) inside w × h. Pure. */
export function centreCrop(w: number, h: number, aspect = PAGE_ASPECT): { x: number; y: number; w: number; h: number } {
  if (w / h > aspect) {
    const cw = Math.round(h * aspect);
    return { x: Math.round((w - cw) / 2), y: 0, w: cw, h };
  }
  const ch = Math.round(w / aspect);
  return { x: 0, y: Math.round((h - ch) / 2), w, h: ch };
}

/** Sample times (seconds), skipping the first and last half-second. Pure. */
export function sampleTimes(duration: number, n = SAMPLE_FRAMES): number[] {
  const start = Math.min(0.5, duration / 4), end = Math.max(start, duration - Math.min(0.5, duration / 4));
  if (n <= 1 || end <= start) return [start];
  return Array.from({ length: n }, (_, i) => start + ((end - start) * i) / (n - 1));
}

function seek(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((ok, ko) => {
    const done = () => { video.removeEventListener('seeked', done); ok(); };
    video.addEventListener('seeked', done);
    video.addEventListener('error', () => ko(new Error('video error')), { once: true });
    video.currentTime = t;
  });
}

/**
 * The sharpest frame of a video file, cropped to the page's shape, as a JPEG
 * blob + the time it was taken at. Browser only.
 */
export async function bestStill(file: File | Blob, onProgress?: (p: number) => void): Promise<{ blob: Blob; at: number; width: number; height: number }> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true; video.playsInline = true; video.preload = 'auto'; video.src = url;
  try {
    await new Promise<void>((ok, ko) => {
      video.onloadeddata = () => ok();
      video.onerror = () => ko(new Error('Cette vidéo ne s’ouvre pas dans le navigateur (format). Envoyez-la en MP4.'));
    });
    const vw = video.videoWidth, vh = video.videoHeight;
    const crop = centreCrop(vw, vh);
    const small = document.createElement('canvas');
    small.width = 240; small.height = Math.round(240 / PAGE_ASPECT);
    const sctx = small.getContext('2d', { willReadFrequently: true })!;
    const times = sampleTimes(video.duration || 1);
    let best = { t: times[0], score: -1 };
    for (let k = 0; k < times.length; k++) {
      await seek(video, times[k]);
      sctx.drawImage(video, crop.x, crop.y, crop.w, crop.h, 0, 0, small.width, small.height);
      const px = sctx.getImageData(0, 0, small.width, small.height).data;
      const gray = new Uint8ClampedArray(small.width * small.height);
      for (let i = 0, j = 0; i < px.length; i += 4, j++) gray[j] = (px[i] * 299 + px[i + 1] * 587 + px[i + 2] * 114) / 1000;
      const s = sharpness(gray, small.width, small.height);
      if (s > best.score) best = { t: times[k], score: s };
      onProgress?.((k + 1) / times.length);
    }
    await seek(video, best.t);
    const scale = Math.min(1, STILL_MAX_H / crop.h);
    const out = document.createElement('canvas');
    out.width = Math.round(crop.w * scale); out.height = Math.round(crop.h * scale);
    out.getContext('2d')!.drawImage(video, crop.x, crop.y, crop.w, crop.h, 0, 0, out.width, out.height);
    const blob = await new Promise<Blob>((ok, ko) => out.toBlob((b) => (b ? ok(b) : ko(new Error('jpeg failed'))), 'image/jpeg', 0.92));
    return { blob, at: best.t, width: out.width, height: out.height };
  } finally {
    URL.revokeObjectURL(url);
  }
}
